import { FC, useEffect } from 'react';
import { AuthContainer, Container } from '../ui/common';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import timeGridPlugin from '@fullcalendar/timegrid';
import { useStore } from '@nanostores/react';
import { Modal, message } from 'antd';
import dayjs from 'dayjs';
import {
  $events,
  $eventsIsLoading,
  $playersIsLoading,
  $selectedPreviewEvent,
  $plannerModalIsOpen,
  $selectedEvent,
  $managePlayersModalIsOpen,
  $manageTrainingTypesModalIsOpen,
  $manageCompetitionTypesModalIsOpen,
  getEvents,
  getPlayers,
  getTrainingTypes,
  getCompetitionTypes,
  saveEvent,
  PlannerEvent,
} from '../store/planner';
import './planner.css';
import { PlannerEventPreviewModal } from './PlannerEventPreviewModal';
import { PlanFormPage } from './PlanFormPage';
import { ManagePlayersModal } from './ManagePlayersModal';
import { ManageTrainingTypesModal } from './ManageTrainingTypesModal';
import { ManageCompetitionTypesModal } from './ManageCompetitionTypesModal';
import { EventDetailsModal } from './EventDetailsModal';
import styled from 'styled-components';
import {
  white,
  yellow,
  black,
  lighterBlueBackground,
  plannerAccent,
  plannerAccentHover,
  plannerAccentRgb,
} from '../components/colors';
import {
  XMarkIcon,
  UserGroupIcon,
  AcademicCapIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline';
import { CloseButton } from './PlanFormPage.style';
import { useNavigate } from 'react-router-dom';

const FloatingToolbar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  background: transparent;
`;

const ToolbarGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const ToolbarButton = styled.button`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: transparent;
  border: 2px solid ${white};
  color: ${white};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    transform 0.2s,
    background-color 0.2s;

  &:hover {
    transform: scale(1.1);
    background-color: rgba(255, 255, 255, 0.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const AddEventButton = styled.button`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: ${yellow};
  border: none;
  color: #000;
  font-size: 28px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const PlannerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  position: sticky;
  top: 0;
  background-color: rgba(0, 0, 0, 0.5);
  padding: 10px 20px;
  box-sizing: border-box;
  z-index: 10;
  color: white;
  height: 74px;
`;

export const PlannerPage: FC = () => {
  const events = useStore($events);
  const eventsLoading = useStore($eventsIsLoading);
  const playersLoading = useStore($playersIsLoading);

  const navigation = useNavigate();

  useEffect(() => {
    void getEvents();
    void getPlayers();
    void getTrainingTypes();
    void getCompetitionTypes();
  }, []);

  const openPlannerModal = () => {
    $selectedEvent.set(null); // Clear for new event
    $plannerModalIsOpen.set(true);
  };

  const handleEventClick = (clickInfo: any) => {
    const event = clickInfo.event;
    const eventData = events.find((e) => e.id === event.id);
    if (eventData) {
      $selectedPreviewEvent.set(eventData);
    }
  };

  const formatDateTime = (date: Date | null) => {
    if (!date) return '';
    return dayjs(date).format('YYYY-MM-DD HH:mm');
  };

  const handleEventReschedule = (info: any) => {
    const oldStart = info.oldEvent?.start as Date | null;
    const newStart = info.event.start as Date | null;
    const title = info.event.title || '';

    Modal.confirm({
      title: 'Reschedule Event',
      content: `Move "${title}" from ${formatDateTime(oldStart)} to ${formatDateTime(newStart)}?`,
      okText: 'Confirm',
      cancelText: 'Cancel',
      onCancel() {
        info.revert();
      },
      async onOk() {
        const storeEvent = $events.get().find((e) => e.id === info.event.id);
        if (!storeEvent) {
          info.revert();
          message.error('Event not found.');
          return;
        }
        const updatedEvent = {
          ...storeEvent,
          start: info.event.start,
          end: info.event.end,
          allDay: info.event.allDay,
          synced: false,
        };
        try {
          await saveEvent(updatedEvent);
        } catch (e) {
          console.error('handleEventReschedule::error', e);
          info.revert();
          message.error('Failed to reschedule event.');
        }
      },
    });
  };

  return (
    <AuthContainer
      style={{
        flexDirection: 'column',
        // Planner page background: client blue for handball, unchanged blue for
        // waterpolo (lighterBlueBackground == the waterpolo authGradientStart,
        // so waterpolo renders identically; handball becomes #2f5fa5 blue).
        backgroundImage: `radial-gradient(${lighterBlueBackground} 0%, ${black} 100%)`,
      }}
    >
      <PlannerHeader>
        <h2>Planner</h2>
        {/* Close */}
        <CloseButton
          onClick={() => {
            navigation('/');
          }}
        >
          <div>
            <XMarkIcon
              style={{
                color: '#fff',
                height: '30px',
                width: '30px',
              }}
              fontSize={30}
            />
          </div>
        </CloseButton>
      </PlannerHeader>
      <Container style={{ boxSizing: 'border-box' }}>
        <div
          style={
            {
              width: '100%',
              // Sport-gated FullCalendar accent (planner.css reads these vars).
              // Green for handball, blue for waterpolo — see colors.ts.
              '--planner-accent': plannerAccent,
              '--planner-accent-hover': plannerAccentHover,
              '--planner-today-bg': `rgba(${plannerAccentRgb}, 0.15)`,
              '--planner-today-bg-faint': `rgba(${plannerAccentRgb}, 0.1)`,
              '--planner-highlight-bg': `rgba(${plannerAccentRgb}, 0.3)`,
            } as React.CSSProperties
          }
        >
          <FullCalendar
            plugins={[
              dayGridPlugin,
              interactionPlugin,
              listPlugin,
              timeGridPlugin,
            ]}
            headerToolbar={{
              start: 'title',
              center: '',
              end: 'today prev,next,dayGridMonth,timeGridWeek,timeGridDay listMonth,listYear',
            }}
            buttonText={{
              listMonth: 'All month',
              listYear: 'All year',
            }}
            events={events}
            eventContent={(eventInfo) => {
              const { description, planType, competitionType, trainingTypes } =
                eventInfo.event.extendedProps;
              const dotColor =
                eventInfo.event.backgroundColor ||
                eventInfo.event.borderColor ||
                '';
              const viewType = eventInfo.view.type;
              const isListView = viewType.startsWith('list');
              const isMonthView = viewType === 'dayGridMonth';

              // Month view: day cells are short, so the full multi-line block
              // never fits and collapses straight to "+N more" (showing zero
              // events). Render a compact single line instead — colour dot +
              // start time + ellipsised title — so every day always shows what
              // kind of event it holds.
              if (isMonthView) {
                return (
                  <div className="fc-event-custom fc-event-compact">
                    {dotColor && (
                      <span
                        className="fc-list-event-dot"
                        style={{ borderColor: dotColor }}
                      />
                    )}
                    {eventInfo.timeText && (
                      <b className="fc-event-compact-time">
                        {eventInfo.timeText}
                      </b>
                    )}
                    <span className="fc-event-compact-title">
                      {eventInfo.event.title}
                    </span>
                  </div>
                );
              }

              return (
                <div className="fc-event-custom">
                  <div className="fc-event-custom-title">
                    {dotColor && !isListView && (
                      <span
                        className="fc-list-event-dot"
                        style={{ borderColor: dotColor, marginRight: '6px' }}
                      />
                    )}
                    {eventInfo.timeText && <b>{eventInfo.timeText} </b>}
                  </div>
                  <div>{eventInfo.event.title}</div>
                  {description && (
                    <div className="fc-event-custom-desc">{description}</div>
                  )}
                  {planType === 'match' && competitionType && (
                    <div className="fc-event-custom-meta">
                      {competitionType}
                    </div>
                  )}
                  {planType === 'training' &&
                    trainingTypes &&
                    trainingTypes.length > 0 && (
                      <div className="fc-event-custom-meta">
                        {trainingTypes.join(', ')}
                      </div>
                    )}
                </div>
              );
            }}
            selectable={true}
            selectMirror={true}
            longPressDelay={300}
            selectLongPressDelay={500}
            initialView={'dayGridMonth'}
            editable={true}
            eventStartEditable={true}
            eventDurationEditable={false}
            eventDrop={handleEventReschedule}
            expandRows={true}
            firstDay={1}
            eventTimeFormat={{
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            }}
            slotLabelFormat={{
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            }}
            height={'calc(100vh - 74px - 72px)'}
            handleWindowResize={true}
            windowResizeDelay={100}
            stickyHeaderDates={true}
            dayMaxEvents={true}
            eventClick={handleEventClick}
            select={(selectInfo) => {
              const calendar = selectInfo.view.calendar;
              const eventsInRange = calendar.getEvents().filter((event) => {
                const eventStart = event.start!;
                const eventEnd = event.end || event.start!;

                // Ellenőrzi, hogy az esemény átfedésben van-e a kijelölt időszakkal
                return (
                  eventStart < selectInfo.end && eventEnd > selectInfo.start
                );
              });

              console.log('Events selectInfo:', selectInfo);

              if (eventsInRange.length > 0) {
              } else {
                $selectedPreviewEvent.set({
                  start: selectInfo.start,
                  end: selectInfo.end,
                } as PlannerEvent);
              }
            }}
          />
        </div>
      </Container>
      <FloatingToolbar>
        <ToolbarGroup>
          <ToolbarButton
            onClick={() => $manageCompetitionTypesModalIsOpen.set(true)}
            title="Manage Competition Types"
          >
            <TrophyIcon style={{ width: 24, height: 24 }} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => $manageTrainingTypesModalIsOpen.set(true)}
            title="Manage Training Types"
          >
            <AcademicCapIcon style={{ width: 24, height: 24 }} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => $managePlayersModalIsOpen.set(true)}
            title="Manage Players"
          >
            <UserGroupIcon style={{ width: 24, height: 24 }} />
          </ToolbarButton>
        </ToolbarGroup>
        <ToolbarGroup>
          <AddEventButton onClick={openPlannerModal} title="Add New Event">
            +
          </AddEventButton>
        </ToolbarGroup>
      </FloatingToolbar>
      <PlannerEventPreviewModal />
      <EventDetailsModal />
      <PlanFormPage />
      <ManagePlayersModal />
      <ManageTrainingTypesModal />
      <ManageCompetitionTypesModal />
    </AuthContainer>
  );
};
